import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Dashboard } from "../../services/api/api";

interface DashboardTabsProps {
  dashboards: Dashboard[];
  activeDashboardId: string | null;
  onTabChange: (dashboardId: string) => void;
  onCreateNew: () => void;
  onRename: (dashboard: Dashboard) => void;
  onSetDefault: (dashboardId: string) => void;
  onDelete: (dashboardId: string) => void;
}

const DashboardTabs: React.FC<DashboardTabsProps> = ({
  dashboards,
  activeDashboardId,
  onTabChange,
  onCreateNew,
  onRename,
  onSetDefault,
  onDelete,
}) => {
  const [contextMenu, setContextMenu] = useState<{
    anchorEl: HTMLElement | null;
    dashboard: Dashboard | null;
  }>({ anchorEl: null, dashboard: null });

  const handleContextMenu = (
    event: React.MouseEvent<HTMLElement>,
    dashboard: Dashboard
  ) => {
    event.stopPropagation();
    setContextMenu({ anchorEl: event.currentTarget, dashboard });
  };

  const handleCloseMenu = () => {
    setContextMenu({ anchorEl: null, dashboard: null });
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleCloseMenu();
  };

  const activeIndex = dashboards.findIndex((d) => d.id === activeDashboardId);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        borderBottom: 1,
        borderColor: "divider",
        mb: 2,
      }}
    >
      <Tabs
        value={activeIndex >= 0 ? activeIndex : 0}
        onChange={(_, newValue) => {
          if (dashboards[newValue]) {
            onTabChange(dashboards[newValue].id);
          }
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: 1,
          minHeight: 48,
          "& .MuiTab-root": {
            minHeight: 48,
            textTransform: "none",
            fontSize: "0.95rem",
          },
        }}
      >
        {dashboards.map((dashboard) => (
          <Tab
            key={dashboard.id}
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {dashboard.isDefault && (
                  <Tooltip title="Default Dashboard">
                    <StarIcon
                      sx={{
                        fontSize: 16,
                        color: "warning.main",
                      }}
                    />
                  </Tooltip>
                )}
                <Typography
                  component="span"
                  sx={{
                    maxWidth: 150,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dashboard.name}
                </Typography>
                <Box
                  component="span"
                  onClick={(e: React.MouseEvent<HTMLElement>) =>
                    handleContextMenu(e, dashboard)
                  }
                  sx={{
                    ml: 0.5,
                    p: 0.25,
                    opacity: 0.7,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: "50%",
                    "&:hover": {
                      opacity: 1,
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            }
            sx={{
              pr: 0.5,
            }}
          />
        ))}
      </Tabs>

      <Tooltip title="Create New Dashboard">
        <IconButton onClick={onCreateNew} color="primary" sx={{ ml: 1 }}>
          <AddIcon />
        </IconButton>
      </Tooltip>

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenu.anchorEl}
        open={Boolean(contextMenu.anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() =>
            contextMenu.dashboard &&
            handleMenuAction(() => onRename(contextMenu.dashboard!))
          }
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            contextMenu.dashboard &&
            handleMenuAction(() => onSetDefault(contextMenu.dashboard!.id))
          }
          disabled={contextMenu.dashboard?.isDefault}
        >
          <ListItemIcon>
            {contextMenu.dashboard?.isDefault ? (
              <StarIcon fontSize="small" color="warning" />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {contextMenu.dashboard?.isDefault
              ? "Default Dashboard"
              : "Set as Default"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            contextMenu.dashboard &&
            handleMenuAction(() => onDelete(contextMenu.dashboard!.id))
          }
          disabled={dashboards.length <= 1}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardTabs;
